import React, { useState } from "react";
import {
  Checkbox,
  Field,
  Input,
  Radio,
  RadioGroup,
  Text,
} from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  useZendeskSourceSettingsText,
  useZendeskSourceSettingsViewModel,
  type ZendeskMode,
} from "@onetasklist/shared/sources/zendesk";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface ZendeskSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const ZendeskSourceSettings: React.FC<ZendeskSourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
}) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useZendeskSourceSettingsText(t);
  const vm = useZendeskSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [statusesQuery, setStatusesQuery] = useState("");
  const [prioritiesQuery, setPrioritiesQuery] = useState("");
  const [ticketTypesQuery, setTicketTypesQuery] = useState("");
  const [viewQuery, setViewQuery] = useState("");
  const [doneStatusQuery, setDoneStatusQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  return (
    <>
      <SettingsSectionCard
        sectionOffset={sectionOffset}
        title={t("board.connections.catalogue.sourceSettings.zendesk.sectionMode")}
      >
        {vm.hasAccountHost && (
          <Field
            orientation="horizontal"
            label={text.accountLabelText}
            hint={text.accountHintText}
          >
            <Text>{vm.accountHost}</Text>
          </Field>
        )}

        <Field
          orientation="horizontal"
          label={text.modeLabelText}
          hint={text.modeHintText}
        >
          <RadioGroup
            value={vm.mode}
            onChange={(_, data) => vm.onSelectMode(data.value as ZendeskMode)}
          >
            <Radio value="Filter" label={text.filterModeLabelText} />
            <Radio value="View" label={text.viewModeLabelText} />
          </RadioGroup>
        </Field>
      </SettingsSectionCard>

      <SettingsSectionCard
        sectionOffset={sectionOffset + 1}
        title={t(
          vm.isViewMode
            ? "board.connections.catalogue.sourceSettings.zendesk.sectionView"
            : "board.connections.catalogue.sourceSettings.zendesk.sectionFilters",
        )}
      >
        {vm.isFilterMode && (
          <>
            <Field
              orientation="horizontal"
              label={text.statusesLabelText}
              hint={text.statusesHintText}
              validationMessage={vm.isStatusesMissing ? text.statusesRequiredText : undefined}
            >
              <SourceMultiTagPicker
                selectedIds={vm.selectedStatuses}
                query={statusesQuery}
                placeholder={text.statusesPlaceholderText}
                options={vm.statusOptions.map((status) => ({
                  id: status,
                  label: text.statusLabels[status],
                }))}
                onQueryChange={setStatusesQuery}
                onSelectionChange={vm.onSelectStatuses}
                minQueryLength={0}
                typeToSearchText={text.statusesHintText}
              />
            </Field>

            <Field
              orientation="horizontal"
              label={text.prioritiesLabelText}
              hint={text.prioritiesHintText}
            >
              <SourceMultiTagPicker
                selectedIds={vm.selectedPriorities}
                query={prioritiesQuery}
                placeholder={text.prioritiesPlaceholderText}
                options={vm.priorityOptions.map((priority) => ({
                  id: priority,
                  label: text.priorityLabels[priority],
                }))}
                onQueryChange={setPrioritiesQuery}
                onSelectionChange={vm.onSelectPriorities}
                minQueryLength={0}
                typeToSearchText={text.prioritiesHintText}
              />
            </Field>

            <Field
              orientation="horizontal"
              label={text.ticketTypesLabelText}
              hint={text.ticketTypesHintText}
            >
              <SourceMultiTagPicker
                selectedIds={vm.selectedTicketTypes}
                query={ticketTypesQuery}
                placeholder={text.ticketTypesPlaceholderText}
                options={vm.ticketTypeOptions.map((ticketType) => ({
                  id: ticketType,
                  label: text.ticketTypeLabels[ticketType],
                }))}
                onQueryChange={setTicketTypesQuery}
                onSelectionChange={vm.onSelectTicketTypes}
                minQueryLength={0}
                typeToSearchText={text.ticketTypesHintText}
              />
            </Field>

            <Field
              orientation="horizontal"
              label={text.assignedToMeLabelText}
              hint={text.assignedToMeHintText}
            >
              <Checkbox
                checked={vm.assignedToMe}
                onChange={(_, data) => vm.onToggleAssignedToMe(!!data.checked)}
              />
            </Field>

            <Field
              orientation="horizontal"
              label={text.tagsLabelText}
              hint={text.tagsHintText}
            >
              <Input
                value={vm.tags}
                placeholder={text.tagsPlaceholderText}
                onChange={(_, data) => vm.onChangeTags(data.value)}
              />
            </Field>
          </>
        )}

        {vm.isViewMode && (
          <Field
            orientation="horizontal"
            label={text.viewLabelText}
            hint={text.viewHintText}
            validationMessage={vm.isViewMissing ? text.viewRequiredText : undefined}
          >
            <SourceSingleTagPicker
              isLoading={vm.isLoadingViews}
              selectedId={
                vm.selectedViewId === vm.noneOptionValue ? undefined : vm.selectedViewId
              }
              selectedLabel={vm.selectedViewTitle}
              query={viewQuery}
              placeholder={text.viewPlaceholderText}
              options={vm.views.map((view) => ({
                id: String(view.id),
                label: view.title,
              }))}
              onQueryChange={setViewQuery}
              onSelect={vm.onSelectView}
              clearSelectionText={text.viewPlaceholderText}
            />
            {!vm.isLoadingViews && !vm.hasError && vm.views.length === 0 && (
              <Text>{text.noViewsText}</Text>
            )}
          </Field>
        )}

        <Field
          orientation="horizontal"
          label={text.doneStatusLabelText}
          hint={text.doneStatusHintText}
          validationMessage={vm.isDoneStatusMissing ? text.doneStatusRequiredText : undefined}
        >
          <SourceSingleTagPicker
            selectedId={vm.selectedDoneStatus ?? undefined}
            selectedLabel={
              vm.selectedDoneStatus ? text.statusLabels[vm.selectedDoneStatus] : undefined
            }
            query={doneStatusQuery}
            placeholder={text.doneStatusPlaceholderText}
            options={vm.doneStatusOptions.map((status) => ({
              id: status,
              label: text.statusLabels[status],
            }))}
            onQueryChange={setDoneStatusQuery}
            onSelect={vm.onSelectDoneStatus}
            clearSelectionText={text.doneStatusPlaceholderText}
          />
        </Field>

        {vm.isLoadingViews && <Text>{text.loadingViewsText}</Text>}
        {vm.hasError && <Text>{text.loadErrorText}</Text>}
      </SettingsSectionCard>
    </>
  );
};
